/**
 * Entry point. Installs the bridge on `window.RafeekhMedia`.
 *
 * A global rather than an ES module export because the consumer is Dart, not
 * JavaScript: `dart:js_interop` binds to properties on `window`, and Flutter
 * web has no module graph to import into.
 *
 * Installing is idempotent and never overwrites a newer bridge -- during a
 * hot reload the page can end up loading this twice, and clobbering a live
 * bridge would strip the poller handles the Dart side still holds.
 */

import { BRIDGE_VERSION, type RafeekhMediaBridge } from './types.js';
import { screenShare } from './screenshare.js';
import { stats } from './stats.js';
import { autoplay } from './autoplay.js';
import { hls } from './hls.js';
import { promptForDevice, watchAvailability } from './remote-playback.js';

export const bridge: RafeekhMediaBridge = {
  version: BRIDGE_VERSION,
  screenShare,
  stats,
  autoplay,
  hls,
  remote: { watchAvailability, prompt: promptForDevice },
};

export function install(target: Window = window): RafeekhMediaBridge {
  const existing = target.RafeekhMedia;
  if (existing && existing.version >= BRIDGE_VERSION) {
    return existing;
  }
  target.RafeekhMedia = bridge;
  return bridge;
}

if (typeof window !== 'undefined') {
  install(window);
}

export { BRIDGE_VERSION } from './types.js';
export type * from './types.js';
